/**
 * Language Configuration - Single Source of Truth
 * All language mappings centralized here
 * Used by: App, roadmapService, youtubeService, articleService
 */

type LanguageAliases = readonly string[];

export const LANGUAGE_CONFIG: Record<string, { 
  code: string; 
  name: string; 
  nativeName: string;
  aliases: string[];
}> = {
  en: { 
    code: 'en', 
    name: 'English', 
    nativeName: 'English',
    aliases: ['english']
  },
  bn: { 
    code: 'bn', 
    name: 'Bengali', 
    nativeName: 'বাংলা',
    aliases: ['bengali', 'bangla']
  },
  hi: { 
    code: 'hi', 
    name: 'Hindi', 
    nativeName: 'हिंदी',
    aliases: ['hindi']
  },
  es: { 
    code: 'es', 
    name: 'Spanish', 
    nativeName: 'Español',
    aliases: ['spanish', 'espanol']
  },
  fr: { 
    code: 'fr', 
    name: 'French', 
    nativeName: 'Français',
    aliases: ['french']
  },
  de: { 
    code: 'de', 
    name: 'German', 
    nativeName: 'Deutsch',
    aliases: ['german', 'deutsch']
  },
  ja: { 
    code: 'ja', 
    name: 'Japanese', 
    nativeName: '日本語',
    aliases: ['japanese', 'nihongo']
  },
  ar: { 
    code: 'ar', 
    name: 'Arabic', 
    nativeName: 'العربية',
    aliases: ['arabic']
  },
};

export type LanguageCode = keyof typeof LANGUAGE_CONFIG;

export interface LanguageInfo {
  code: LanguageCode;
  name: string;
  nativeName: string;
}

/**
 * Normalize any language input (code or name) to canonical format
 * Handles variations: 'en', 'english', 'English', 'EN' all map to { code: 'en', name: 'English' }
 */
export function normalizeLanguage(input: string | undefined | null): LanguageInfo {
  const normalized = (input || 'en').toLowerCase().trim();

  // Direct code match
  const config = LANGUAGE_CONFIG[normalized as LanguageCode];
  if (config) {
    return {
      code: normalized as LanguageCode,
      name: config.name,
      nativeName: config.nativeName
    };
  }

  // Check aliases
  for (const [code, config] of Object.entries(LANGUAGE_CONFIG)) {
    if (config.aliases.includes(normalized)) {
      return {
        code: code as LanguageCode,
        name: config.name,
        nativeName: config.nativeName
      };
    }
  }

  // Default to English
  return {
    code: 'en',
    name: LANGUAGE_CONFIG.en.name,
    nativeName: LANGUAGE_CONFIG.en.nativeName
  };
}

/**
 * Get just the language name from any input
 * Used for search terms and display
 */
export function getLanguageName(input: string | undefined | null): string {
  return normalizeLanguage(input).name;
}

/**
 * Get just the language code
 */
export function getLanguageCode(input: string | undefined | null): LanguageCode {
  return normalizeLanguage(input).code;
}

/**
 * Get all available languages as array
 */
export function getAllLanguages(): LanguageInfo[] {
  return Object.entries(LANGUAGE_CONFIG).map(([code, config]) => ({
    code: code as LanguageCode,
    name: config.name,
    nativeName: config.nativeName
  }));
}

/**
 * Check if a language is supported
 */
export function isLanguageSupported(input: string | undefined | null): boolean {
  const normalized = (input || '').toLowerCase().trim();
  
  for (const [code, config] of Object.entries(LANGUAGE_CONFIG)) {
    if (code === normalized || config.aliases.includes(normalized)) {
      return true;
    }
  }
  
  return false;
}
